/**
 * A value menu: the current value as text with a 12px chevron, opening a
 * menu of choices (settings-pocket spec §5, “Value menus”). Used by the task
 * pocket's rows and the board's Display pocket.
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icons,
} from "@/components/ui";

export interface TaskValueMenuOption<T extends string> {
  value: T;
  label: string;
  destructive?: boolean;
}

export function TaskValueMenu<T extends string>({
  label,
  value,
  options,
  selected,
  onSelect,
  testId,
  disabled = false,
}: {
  /** What the value is, for the accessible name (“Status”). */
  label: string;
  /** The value text on the trigger. */
  value: string;
  options: ReadonlyArray<TaskValueMenuOption<T>>;
  /** Set when the menu picks one value: that option gets the checkmark. */
  selected?: string;
  onSelect: (value: T) => void;
  testId: string;
  disabled?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={`${label}: ${value}`}
          className="flex min-w-0 max-w-[11rem] items-center gap-1 rounded-pill px-2 py-1 text-appkit-body text-muted outline-none transition-colors hover:bg-[var(--color-interactive-hover)] hover:text-primary focus-visible:ring-2 focus-visible:ring-accent data-[state=open]:bg-[var(--color-interactive-hover)] data-[state=open]:text-primary"
          data-testid={testId}
        >
          <span className="truncate" title={value}>
            {value}
          </span>
          <Icons.ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent data-task-pocket-owned="" align="end">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            destructive={option.destructive}
            icon={
              selected === undefined ? undefined : option.value === selected ? (
                <Icons.Check aria-hidden="true" />
              ) : (
                <span aria-hidden="true" />
              )
            }
            onSelect={() => onSelect(option.value)}
            data-testid={`${testId}-option-${option.value}`}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
